// Reassign (spec C6; the Record board's Reassign dialog, Dialog and ConfirmPopup cards): pick who gets
// the renewal (or several, from the bulk bar), a reason, confirm, then a toast with Undo. To a Renewal
// VA right away, or back to the Unassigned pool (Manager, Admin). To an AE it waits for the AE's
// approval (S15): the reason is required, since the AE sees it, a note can go with it, and the
// renewal shows Awaiting AE until they accept. The Manager and the Admin can "Assign to me"; for the
// Account Executive that choice is Take over (S15 batch 7, decision 1: a handoff, status unchanged).
import { useEffect, useRef, useState } from 'react';
import type { Api, Assignees, ReassignReason } from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Dialog } from '../shell/Dialog';
import { noteMax } from './notes-lines';
import { CheckIcon, ChevronDownIcon } from '../shell/icons';
import { takeOverConfirm, tookOverText } from './ae-handoff-lines';
import {
  aeConfirmCopy,
  confirmCopy,
  heldBy,
  plural,
  reassignedText,
  reasonLabels,
  sentForApprovalText,
  type Reassigned,
  type Row,
} from './reassign-lines';

/** Unassigned is a pool, never a person (spec C6). */
const POOL = 'pool';
/** The person reassigning, taking the renewal themselves. */
const ME = 'me';

export function ReassignDialog({
  api,
  rows,
  me,
  onClose,
  onDone,
}: {
  api: Api;
  rows: Row[];
  me: { id: string; name: string };
  onClose: () => void;
  onDone: (r: Reassigned) => void;
}) {
  const [people, setPeople] = useState<Assignees | null>(null);
  const [failed, setFailed] = useState(false);
  const [pick, setPick] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [reason, setReason] = useState<ReassignReason | null>(null);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.assignees().then(
      (p) => live && setPeople(p),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [api]);

  const one = rows.length === 1 ? rows[0] : null;
  const subline = one
    ? [one.insured, one.carrier ?? 'Carrier not in AMS360', one.line, one.policyNumber].join(' | ')
    : `${plural(rows.length)} selected`;
  // Only the AE's own option when the person may not give work to others (the AE).
  const onlyOwn = !!people && people.canTakeOwn && !people.canUnassign;
  const vas = onlyOwn ? [] : (people?.people.filter((p) => p.role === 'renewal_va') ?? []);
  const aes = onlyOwn
    ? []
    : (people?.people.filter((p) => p.role === 'account_executive' && p.id !== me.id) ?? []);
  // An AE accepts first (spec C6): Awaiting AE until then.
  const toAe = aes.find((p) => p.id === pick) ?? null;
  const to =
    pick === ME
      ? { id: me.id, name: me.name }
      : pick && pick !== POOL
        ? (vas.find((p) => p.id === pick) ?? toAe)
        : null;
  // The AE sees the reason, so it is required for an AE and optional between VAs.
  const ready = !!pick && (!toAe || !!reason);
  // The AE's own choice is Take over (decision 1), everywhere the AE could Assign to me.
  const mine = onlyOwn ? `Take over (${me.name})` : `Assign to me (${me.name})`;
  const label =
    pick === POOL
      ? 'Return to the Unassigned pool'
      : pick === ME
        ? mine
        : (to?.name ?? 'Pick a person');
  const allUnassigned = rows.every((r) => r.handledBy === null);
  const allMine = rows.every((r) => r.handledBy?.id === me.id);
  // The AE takes only from the pool or a Renewal VA.
  const held = onlyOwn && people ? heldBy(rows, people.people, me.id) : null;

  // Two fast clicks land before the disabled button renders: only the first sends.
  const sending = useRef(false);
  const submitToAe = async (ae: { id: string; name: string }, why: ReassignReason) => {
    const sent: string[] = [];
    const handoffIds: string[] = [];
    // One request per renewal; a renewal changed by someone else is left as it was.
    for (const r of rows) {
      const res = await api.createHandoff(r.id, {
        kind: 'reassign_to_ae',
        reassignReason: why,
        ...(note.trim() ? { note: note.trim() } : {}),
        to: ae.id,
        from: r.handledBy?.id ?? null,
      });
      if (res.ok) {
        sent.push(r.id);
        handoffIds.push(res.body.handoff.id);
      }
    }
    return { sent, handoffIds };
  };

  const submit = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setError(null);
    if (toAe && reason) {
      const { sent, handoffIds } = await submitToAe(toAe, reason).finally(() => {
        sending.current = false;
      });
      setBusy(false);
      if (sent.length === 0) {
        setConfirming(false);
        setError('Could not send it to the AE. Someone may have changed it. Try again.');
        return;
      }
      onDone({
        text: sentForApprovalText(rows, toAe.name, sent),
        undo: null,
        undoHandoffs: handoffIds,
      });
      return;
    }
    if (onlyOwn && pick === ME) {
      // Take over, one handoff each; a renewal changed by someone else is left as it was.
      const taken: string[] = [];
      for (const r of rows) {
        const res = await api.createHandoff(r.id, {
          kind: 'take_over',
          from: r.handledBy?.id ?? null,
        });
        if (res.ok) taken.push(res.body.handoff.id);
      }
      sending.current = false;
      setBusy(false);
      if (taken.length === 0) {
        setConfirming(false);
        setError('Could not take it over. Someone may have changed it. Try again.');
        return;
      }
      onDone({ text: tookOverText(rows, taken.length), undo: null, undoTakeOvers: taken });
      return;
    }
    const target = pick === POOL ? null : pick === ME ? me.id : pick;
    const items = rows.map((r) => ({ id: r.id, from: r.handledBy?.id ?? null, to: target }));
    const res = await api.reassign(items, reason).finally(() => {
      sending.current = false;
    });
    setBusy(false);
    if (!res.ok) {
      setConfirming(false);
      setError('Could not reassign. Try again.');
      return;
    }
    const moved = rows.filter((r) => res.body.moved.includes(r.id));
    onDone({
      text: reassignedText(rows, pick === ME ? 'you' : (to?.name ?? null), res.body),
      undo: moved.length
        ? moved.map((r) => ({ id: r.id, from: target, to: r.handledBy?.id ?? null }))
        : null,
    });
  };

  const option = (id: string, text: string, hint: string | null, disabled = false) => (
    <button
      key={id}
      type="button"
      className="mi"
      role="option"
      aria-selected={pick === id}
      disabled={disabled}
      onClick={() => {
        setPick(id);
        setListOpen(false);
      }}
    >
      <span className="dd-ck">{pick === id && <CheckIcon className="ico s12" />}</span>
      <span className="col g2" style={{ minWidth: 0 }}>
        <span>{text}</span>
        {hint && <span className="sel-h">{hint}</span>}
      </span>
    </button>
  );

  return (
    <>
      <Dialog
        id="ra-t"
        title="Reassign"
        subline={subline}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary ml-auto"
              disabled={!ready || busy}
              onClick={() => setConfirming(true)}
            >
              Reassign
            </button>
          </>
        }
      >
        <div>
          <span className="flab" id="ra-to-l">
            Reassign to <span className="req">*</span>
          </span>
          <div className="sel">
            <button
              type="button"
              className={pick ? 'sel-b' : 'sel-b ph'}
              id="ra-to"
              aria-haspopup="listbox"
              aria-expanded={listOpen}
              aria-labelledby="ra-to-l ra-to"
              disabled={!people}
              onClick={() => setListOpen(!listOpen)}
            >
              <span className="sel-v">
                {people ? label : failed ? 'Could not load' : 'Loading'}
              </span>
              <ChevronDownIcon className="ico s12" />
            </button>
            {listOpen && people && (
              <div className="menu sel-m" role="listbox" aria-label="Reassign to">
                {people.canTakeOwn && (
                  <>
                    {option(
                      ME,
                      mine,
                      allMine ? 'Already yours' : (held ?? 'It moves to your My renewals'),
                      allMine || !!held,
                    )}
                    {!onlyOwn && <div className="vm-div" />}
                  </>
                )}
                {vas.length > 0 && <div className="vm-sec">Renewal VAs</div>}
                {vas.map((p) =>
                  option(
                    p.id,
                    p.name,
                    p.outToday ? 'Out today' : null,
                    !!one && one.handledBy?.id === p.id,
                  ),
                )}
                {aes.length > 0 && <div className="vm-sec">Account executives</div>}
                {aes.map((p) =>
                  option(
                    p.id,
                    p.name,
                    p.outToday ? 'Out today' : 'Accepts first',
                    !!one && one.handledBy?.id === p.id,
                  ),
                )}
                {people.canUnassign && !allUnassigned && (
                  <>
                    <div className="vm-div" />
                    {option(POOL, 'Return to the Unassigned pool', null)}
                  </>
                )}
              </div>
            )}
          </div>
          {toAe ? (
            <p className="fnote">
              The account executive accepts first and sees your reason. Until then the renewal stays
              where it is with an Awaiting AE tag.
            </p>
          ) : (
            to && <p className="fnote">Moves right away.</p>
          )}
          {failed && (
            <p className="ferr">The list of people could not be loaded. Close and try again.</p>
          )}
        </div>
        <div role="radiogroup" aria-labelledby="ra-r">
          <div id="ra-r" className="flab">
            Reason{' '}
            {toAe ? <span className="req">*</span> : <span className="optl">(optional)</span>}
          </div>
          <div className="fopts">
            {(Object.keys(reasonLabels) as ReassignReason[]).map((r) => (
              <button
                key={r}
                type="button"
                className={reason === r ? 'chip on' : 'chip'}
                aria-pressed={reason === r}
                onClick={() => setReason(reason === r ? null : r)}
              >
                {reasonLabels[r]}
              </button>
            ))}
          </div>
          <p className="fnote">
            {toAe
              ? 'The AE sees it with the request.'
              : 'Kept in the Portal log with who moved it and when.'}
          </p>
        </div>
        {/* The note goes with the request to the AE; a move between VAs keeps the reason only. */}
        {toAe && (
          <div>
            <label className="flab" htmlFor="ra-note">
              Note <span className="optl">(optional)</span>
            </label>
            <textarea
              id="ra-note"
              className="input"
              placeholder="Anything they should know"
              maxLength={noteMax}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        )}
        {error && (
          <p className="ferr" role="alert">
            {error}
          </p>
        )}
      </Dialog>
      {confirming && pick && (
        <ConfirmPopup
          {...(toAe
            ? aeConfirmCopy(rows, toAe, me.id)
            : onlyOwn && pick === ME
              ? takeOverConfirm(rows, me.id)
              : confirmCopy(rows, to, me.id))}
          action={
            busy
              ? onlyOwn && pick === ME
                ? 'Taking over'
                : 'Reassigning'
              : pick === POOL
                ? 'Return to pool'
                : pick === ME
                  ? onlyOwn
                    ? 'Take over'
                    : 'Assign to me'
                  : 'Reassign'
          }
          busy={busy}
          onConfirm={() => void submit()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
